import React from "react";
import { createRoot } from "react-dom/client";
import { devDebug } from "./lib/devLog";
import "./index.css";
import "./follow-action.css";
import "./reply-delete-controls.css";
import "./onboarding-background.css";
import "./mobile-composer-fix.css";
import App from "./App";
import { startReplyDeleteControls } from "./lib/replyDeleteControls";
import { startOnboardingVisualPatch } from "./lib/onboardingVisualPatch";
import { startReplyAudioPlaybackFix } from "./lib/replyAudioPlayback";
import { startCommentDeleteLabels } from "./lib/commentDeleteLabels";
import { startSingleAudioPlayback } from "./lib/singleAudioPlayback";
import { startSiteTranslation } from "./lib/siteTranslation";
import { startNativePush } from "./lib/nativePush";

// Emergency self-heal: `?reset=1` in the URL unregisters ALL service workers,
// wipes every Cache Storage entry and clears localStorage/sessionStorage,
// then reloads to a clean URL. Send this link to any user stuck on a
// stale bundle after a redeploy: https://opinione-libera.com/?reset=1
(async () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("reset")) return;
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
    try { window.localStorage.clear(); } catch (e) { devDebug("clear ls", e?.message); }
    try { window.sessionStorage.clear(); } catch (e) { devDebug("clear ss", e?.message); }
    window.location.replace(window.location.origin + "/");
    return;
  } catch (err) {
    devDebug("self-heal reset failed", err?.message);
  }
})();

// Native shells always use the production site. Remove stale HTTP/service-worker
// caches once per installed build so an APK/AAB update cannot reopen an older UI.
// Identity, mnemonic and encrypted key material in localStorage/IndexedDB are
// deliberately preserved.
(async () => {
  try {
    const isNative = Boolean(window.Capacitor?.isNativePlatform?.());
    if (!isNative) return;
    const nativeBuild = "youvoice-native-8";
    if (window.sessionStorage.getItem("ol_native_cache_build") === nativeBuild) return;
    window.sessionStorage.setItem("ol_native_cache_build", nativeBuild);
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)));
    }
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name).catch(() => false)));
    }
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.set("nativeBuild", "8");
    window.location.replace(cleanUrl.toString());
  } catch (error) {
    devDebug("native cache refresh failed", error?.message);
  }
})();

const root = createRoot(document.getElementById("root"));
root.render(<App />);
startSiteTranslation();
startReplyDeleteControls();
startOnboardingVisualPatch();
startReplyAudioPlaybackFix();
startCommentDeleteLabels();
startSingleAudioPlayback();
void startNativePush();

if ("serviceWorker" in navigator && process.env.NODE_ENV === "production" && !window.Capacitor?.isNativePlatform?.()) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        const activate = () => reg.waiting && reg.waiting.postMessage("SKIP_WAITING");
        if (reg.waiting) activate();
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) activate();
          });
        });
      })
      .catch((err) => devDebug("SW registration failed", err));

    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
